import { useEffect, useRef, useState } from "react"
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import IconButton from "@mui/material/IconButton"
import TextField from "@mui/material/TextField"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import SendIcon from "@mui/icons-material/Send"
import { MONO, CHAT_COLOR } from "../constants"
import { scrollbarSx } from "../../../shared/scrollbar"
import { MsgBubble } from "./MsgBubble"

export function ThreadPanel({ root, me, profiles, users, onVaultLink, onClose, onSend }) {
  const [replies, setReplies] = useState([])
  const [input, setInput] = useState("")
  const [loading, setLoading] = useState(true)
  const scrollRef = useRef(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    fetch("/api/messenger/thread/?thread_id=" + encodeURIComponent(root.id))
      .then(r => r.json())
      .then(d => { if (!cancelled) setReplies(d.messages ?? []) })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [root.id])

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight
  }, [replies])

  const send = async () => {
    const text = input.trim()
    if (!text) return
    setInput("")
    const msg = await onSend(root.id, text)
    if (msg) setReplies(prev => [...prev, msg])
  }

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, px: 1.25, py: 0.7,
        borderBottom: "1px solid rgba(var(--app-fg-rgb),0.06)", flexShrink: 0 }}>
        <IconButton size="small" onClick={onClose} sx={{ p: 0, color: "rgba(var(--app-fg-rgb),0.5)" }}>
          <ArrowBackIcon sx={{ fontSize: 15 }} />
        </IconButton>
        <Typography sx={{ color: CHAT_COLOR, fontFamily: MONO, fontSize: "0.7rem", fontWeight: 600 }}>
          Thread
        </Typography>
      </Box>

      <Box sx={{ px: 1.25, py: 0.75, borderBottom: "1px solid rgba(var(--app-fg-rgb),0.05)", flexShrink: 0 }}>
        <MsgBubble msg={root} me={me} profiles={profiles} users={users} onVaultLink={onVaultLink} />
      </Box>

      <Box ref={scrollRef} sx={{ flex: 1, overflowY: "auto", px: 1.25, py: 1, ...scrollbarSx }}>
        {loading && (
          <Typography sx={{ color: "rgba(var(--app-fg-rgb),0.2)", fontFamily: MONO, fontSize: "0.65rem", textAlign: "center" }}>
            Loading…
          </Typography>
        )}
        {!loading && replies.length === 0 && (
          <Typography sx={{ color: "rgba(var(--app-fg-rgb),0.17)", fontFamily: MONO, fontSize: "0.65rem", textAlign: "center" }}>
            No replies yet.
          </Typography>
        )}
        {replies.map(msg => (
          <MsgBubble key={msg.id} msg={msg} me={me} profiles={profiles} users={users} onVaultLink={onVaultLink} />
        ))}
      </Box>

      <Box sx={{ borderTop: "1px solid rgba(var(--app-fg-rgb),0.06)", px: 0.75, py: 0.6,
        display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0, backgroundColor: "var(--app-bg)" }}>
        <TextField fullWidth size="small" multiline maxRows={3}
          value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send() } }}
          placeholder="Reply in thread…"
          sx={{
            "& .MuiInputBase-root": { backgroundColor: "transparent", p: "4px 8px" },
            "& .MuiInputBase-input": { fontFamily: MONO, fontSize: "0.71rem", color: "rgba(var(--app-fg-rgb),0.88)", p: 0 },
            "& .MuiOutlinedInput-root": {
              "& fieldset": { borderColor: "rgba(var(--app-fg-rgb),0.07)" },
              "&:hover fieldset": { borderColor: "rgba(245,158,11,0.22)" },
              "&.Mui-focused fieldset": { borderColor: "rgba(245,158,11,0.42)" },
            },
          }} />
        <IconButton size="small" onClick={send} disabled={!input.trim()}
          sx={{ color: input.trim() ? CHAT_COLOR : "rgba(var(--app-fg-rgb),0.18)", "&:hover": { color: "var(--app-warning)" }, flexShrink: 0 }}>
          <SendIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    </Box>
  )
}
